import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { api } from '@/lib/api'
import { useAuth } from '@/lib/auth-context'

type Ctx = {
  isOnShift: (userId: string | null | undefined) => boolean
  refresh: () => Promise<void>
  ready: boolean
}

const POLL_MS = 45_000

const OnShiftUsersContext = createContext<Ctx | null>(null)

export function OnShiftUsersProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [ids, setIds] = useState<Set<string>>(() => new Set())
  const [ready, setReady] = useState(false)

  const load = useCallback(async () => {
    if (!user) {
      setIds(new Set())
      setReady(true)
      return
    }
    try {
      const r = await api.scheduleOnShiftUserIds()
      setIds(new Set(r.user_ids ?? []))
    } catch {
      setIds(new Set())
    } finally {
      setReady(true)
    }
  }, [user])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (!user) return
    const id = window.setInterval(() => void load(), POLL_MS)
    return () => clearInterval(id)
  }, [user, load])

  useEffect(() => {
    if (!user) return
    const onFocus = () => void load()
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [user, load])

  const isOnShift = useCallback(
    (userId: string | null | undefined) => !!userId && ids.has(userId),
    [ids],
  )

  const value = useMemo(() => ({ isOnShift, refresh: load, ready }), [isOnShift, load, ready])

  return <OnShiftUsersContext.Provider value={value}>{children}</OnShiftUsersContext.Provider>
}

export function useOnShiftUsers(): Ctx {
  const ctx = useContext(OnShiftUsersContext)
  if (!ctx) {
    return {
      isOnShift: () => false,
      refresh: async () => {},
      ready: true,
    }
  }
  return ctx
}
